import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import Tabs from 'components/ui/Tabs/Tabs';
import Skeleton from 'react-loading-skeleton';
import { TermsResponse } from 'ts/shared/interfaces/Generic';
import { getSpecialTerms, getTerms } from 'services/cms';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ScreenshotSupport from './ScreenshotSupport';
import MaximizeModal from './MaximizeModal';
import { checkAHAVI, replaceHtmlToStr } from 'utils/utils';
import useDraggableModal from 'hooks/useDraggableModal';

interface GeneralConditionsModalProps {
  onClose: () => void;
  product?: string;
  name?: string;
  package?: any;
}

const GeneralConditionsModal: FC<GeneralConditionsModalProps> = ({ onClose, ...props }) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [conditions, setConditions] = useState<TermsResponse>();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [packageConditions, setPackageConditions] = useState<any[]>();

  useEffect(() => {
    (async () => {
      const newConditions = await getTerms(props.product, props.name);

      setConditions(newConditions);
    })();

    if (props.package) {
      (async () => {
        const newConditions = await getSpecialTerms(props.package!);

        setPackageConditions(newConditions);
      })();
    }
  }, []); // eslint-disable-line

  return (
    <div
      className="modal-overlay modal-overlay-top modal-maximizable"
      ref={modalOverlayRef}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div ref={modalRef} className={'modal modal-sm active'} style={modalStyle}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">{t('package.general_conditions')}</h5>
          <div className="display-flex" data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => onClose()} />
          </div>
        </div>
        <div
          className={
            'modal-content text-sm ' + (checkAHAVI(conditions?.title?.body || '') ? 'rtl' : '')
          }
        >
          {!!conditions?.tabs?.length && (
            <Fragment>
              <Tabs
                label={t('package.general_conditions')}
                value={String(activeTabIndex)}
                onValueChange={(value) => setActiveTabIndex(Number(value))}
                items={[
                  ...conditions.tabs.map((condition, index) => ({
                    value: String(index),
                    label: condition.title.desc,
                  })),
                  ...(packageConditions?.length
                    ? [{ value: String(conditions.tabs.length), label: t('package.supplier_terms') }]
                    : []),
                ]}
              />

              <div className="wrapper">
                {activeTabIndex < conditions.tabs.length && (
                  <div
                    className="content"
                    dangerouslySetInnerHTML={{ __html: conditions.tabs[activeTabIndex].title.body }}
                  ></div>
                )}
                {activeTabIndex === conditions.tabs.length && (
                  <div className="content">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: replaceHtmlToStr(packageConditions?.[0]?.desc || ''),
                      }}
                    ></div>
                  </div>
                )}
              </div>
            </Fragment>
          )}
          {!conditions && (
            <Fragment>
              <Tabs
                label={t('package.general_conditions')}
                value="0"
                items={[
                  { value: '0', label: <Skeleton width={220} height={30} /> },
                  { value: '1', label: <Skeleton width={220} height={30} />, disabled: true },
                ]}
              />
              <div className="wrapper">
                <div className="penalty" style={{ marginRight: 10 }}>
                  <Skeleton height={22} width={120} />
                </div>
                <div className="content">
                  <Skeleton height={22} width={220} className="mb-5" />
                  <Skeleton height={22} width={230} className="mb-5" />
                  <Skeleton height={22} width={220} className="mb-5" />
                  <Skeleton height={22} width={230} className="mb-5" />
                  <Skeleton height={22} width={220} className="mb-5" />
                  <Skeleton height={22} width={230} className="mb-5" />
                  <Skeleton height={22} width={220} className="mb-5" />
                  <Skeleton height={22} width={230} className="mb-5" />
                  <Skeleton height={22} width={220} className="mb-5" />
                  <Skeleton height={22} width={230} className="mb-5" />
                </div>
              </div>
            </Fragment>
          )}
          {!!conditions?.title && !conditions?.tabs?.length && (
            <div>
              <h5 className="title">{conditions.title.desc}</h5>
              <div
                className="text-lh"
                style={{ wordWrap: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: conditions.title.body }}
              />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button title={t('close')} className="btn btn-default" onClick={() => onClose()} />
        </div>
      </div>
    </div>
  );
};

export default GeneralConditionsModal;
